import { Label } from '@up2stay/up2promo-ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@up2stay/up2promo-ui';
import { Switch } from '@up2stay/up2promo-ui';
import type { FieldErrors, UseFormSetValue } from 'react-hook-form';

import { UpgradeCard } from '@/core/components/billing/upgrade-surfaces';
import { ProfileEditFieldOverrideHint } from '@/core/components/profile/edit/profile-edit-field-override-hint';
import type { ProfileFormData } from '@/core/components/profile/edit/profile-edit-form-schema';
import {
  ProfileAppearanceFields,
  type ProfileAppearanceDict,
} from '@/core/components/profile/profile-appearance-fields';
import { ProfileAccentColorField } from '@/core/components/profile/profile-accent-color-field';
import { ProfileFieldLockedHint } from '@/core/components/profile-templates/profile-field-locked-hint';
import {
  SettingsGroup,
  SettingsGroupFooter,
  SettingsGroupLabel,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import type { ProfileTemplateFieldKey } from '@/core/lib/profile-template-fields';
import { PROFILE_STYLING_FORM_DEFAULTS } from '@/core/lib/profile-styling-form';

type SettingsDict = {
  title: string;
  description: string;
  language: string;
  languagePlaceholder: string;
  languageAuto: string;
  languageGerman: string;
  languageEnglish: string;
  languageHelper?: string;
  visibility: string;
  visibilityPlaceholder: string;
  visibilityUnlisted: string;
  visibilityPrivate: string;
  showUp2promoBranding: string;
  showUp2promoBrandingDescription: string;
  showUp2promoBrandingUpgradeTitle: string;
  showUp2promoBrandingUpgradeDescription: string;
  accentColor: string;
  accentColorDescription: string;
  accentColorReset: string;
  accentColorPlaceholder: string;
  accentColorCtaPreview: string;
  accentColorCtaAdjustedDarker: string;
  accentColorCtaAdjustedLighter: string;
  accentColorUpgradeTitle: string;
  accentColorUpgradeDescription: string;
  appearance: ProfileAppearanceDict;
};

type ProfileEditSettingsSectionProps = {
  dict: SettingsDict;
  formValues: ProfileFormData;
  errors: FieldErrors<ProfileFormData>;
  setValue: UseFormSetValue<ProfileFormData>;
  resolvedAccentColor: string | null;
  resolvedShowUp2promoBranding: boolean;
  isLocked: (key: ProfileTemplateFieldKey) => boolean;
  formSyncBlocked: boolean;
  entitlementsLoading: boolean;
  canCustomizeAccent: boolean;
  canHideBranding: boolean;
  /** Sample CTA label for the accent preview (public profile strings). */
  accentCtaLabel: string;
  fieldLockedHintMessage: string;
  overrideHintMessage: string;
  resetLabel: string;
  templateValueLabel: string;
  getFieldOverrideDisplay: Parameters<
    typeof ProfileEditFieldOverrideHint
  >[0]['getFieldOverrideDisplay'];
  onResetField: (key: ProfileTemplateFieldKey) => void;
};

export function ProfileEditSettingsSection({
  dict,
  formValues,
  errors,
  setValue,
  resolvedAccentColor,
  resolvedShowUp2promoBranding,
  isLocked,
  formSyncBlocked,
  entitlementsLoading,
  canCustomizeAccent,
  canHideBranding,
  accentCtaLabel,
  fieldLockedHintMessage,
  overrideHintMessage,
  resetLabel,
  templateValueLabel,
  getFieldOverrideDisplay,
  onResetField,
}: ProfileEditSettingsSectionProps) {
  const watchedLanguage = formValues.language;
  const watchedVisibility = formValues.visibility;

  const overrideHintProps = {
    getFieldOverrideDisplay,
    overrideHintMessage,
    resetLabel,
    templateValueLabel,
    onReset: onResetField,
  };

  return (
    <div className="flex min-w-0 w-full flex-col gap-6">
      <div className="flex min-w-0 flex-col gap-2">
        <SettingsGroupLabel>{dict.title}</SettingsGroupLabel>
        <SettingsGroup>
          <SettingsGroupRows>
            <SettingsGroupRow>
              <div className="space-y-0.5 min-w-0 w-full">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor="language">{dict.language}</Label>
                  {dict.languageHelper ? (
                    <SettingsInfoHint label={dict.language}>
                      {dict.languageHelper}
                    </SettingsInfoHint>
                  ) : null}
                </div>
                <ProfileFieldLockedHint
                  locked={isLocked('language')}
                  message={fieldLockedHintMessage}
                />
                <Select
                  value={watchedLanguage}
                  disabled={isLocked('language')}
                  onValueChange={(value) =>
                    setValue('language', value as 'auto' | 'de' | 'en')
                  }
                >
                  <SelectTrigger id="language" className="min-w-0 w-full">
                    <SelectValue placeholder={dict.languagePlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{dict.languageAuto}</SelectItem>
                    <SelectItem value="de">{dict.languageGerman}</SelectItem>
                    <SelectItem value="en">{dict.languageEnglish}</SelectItem>
                  </SelectContent>
                </Select>
                <ProfileEditFieldOverrideHint
                  fieldKey="language"
                  {...overrideHintProps}
                />
                {errors.language ? (
                  <p className="text-sm text-destructive">
                    {errors.language.message}
                  </p>
                ) : null}
              </div>
            </SettingsGroupRow>

            <SettingsGroupRow separated>
              <div className="space-y-0.5 min-w-0">
                <Label htmlFor="visibility">{dict.visibility}</Label>
                <ProfileFieldLockedHint
                  locked={isLocked('visibility')}
                  message={fieldLockedHintMessage}
                />
                <Select
                  value={watchedVisibility}
                  disabled={isLocked('visibility')}
                  onValueChange={(value) =>
                    setValue('visibility', value as 'private' | 'unlisted')
                  }
                >
                  <SelectTrigger id="visibility" className="min-w-0 w-full">
                    <SelectValue placeholder={dict.visibilityPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unlisted">
                      {dict.visibilityUnlisted}
                    </SelectItem>
                    <SelectItem value="private">
                      {dict.visibilityPrivate}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <ProfileEditFieldOverrideHint
                  fieldKey="visibility"
                  {...overrideHintProps}
                />
                {errors.visibility ? (
                  <p className="text-sm text-destructive">
                    {errors.visibility.message}
                  </p>
                ) : null}
              </div>
            </SettingsGroupRow>

            <SettingsGroupRow separated>
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 flex-1 items-center gap-0.5">
                  <Label htmlFor="showUp2promoBranding">
                    {dict.showUp2promoBranding}
                  </Label>
                  <SettingsInfoHint label={dict.showUp2promoBranding}>
                    {dict.showUp2promoBrandingDescription}
                  </SettingsInfoHint>
                  <ProfileFieldLockedHint
                    locked={isLocked('showUp2promoBranding')}
                    message={fieldLockedHintMessage}
                  />
                </div>
                <Switch
                  id="showUp2promoBranding"
                  checked={resolvedShowUp2promoBranding}
                  disabled={
                    isLocked('showUp2promoBranding') ||
                    entitlementsLoading ||
                    !canHideBranding
                  }
                  onCheckedChange={(checked) =>
                    setValue('showUp2promoBranding', checked)
                  }
                />
              </div>
              {!entitlementsLoading && !canHideBranding ? (
                <div className="pt-3">
                  <UpgradeCard
                    feature="feature.profile_branding"
                    compact
                    title={dict.showUp2promoBrandingUpgradeTitle}
                    description={dict.showUp2promoBrandingUpgradeDescription}
                  />
                </div>
              ) : null}
              <ProfileEditFieldOverrideHint
                fieldKey="showUp2promoBranding"
                {...overrideHintProps}
              />
            </SettingsGroupRow>
          </SettingsGroupRows>
        </SettingsGroup>
        <SettingsGroupFooter>{dict.description}</SettingsGroupFooter>
      </div>

      <ProfileAppearanceFields
        dict={dict.appearance}
        disabled={formSyncBlocked}
        values={{
          layout: formValues.layout ?? PROFILE_STYLING_FORM_DEFAULTS.layout,
          theme: formValues.theme ?? PROFILE_STYLING_FORM_DEFAULTS.theme,
          headerStyle:
            formValues.headerStyle ?? PROFILE_STYLING_FORM_DEFAULTS.headerStyle,
          headerImage: formValues.headerImage ?? null,
          buttonShape: formValues.buttonShape ?? null,
        }}
        onChange={(key, value) => setValue(key, value as never)}
      />

      <div className="flex min-w-0 flex-col gap-2">
        <SettingsGroupLabel>{dict.accentColor}</SettingsGroupLabel>
        <SettingsGroup>
          <SettingsGroupRow>
            <ProfileAccentColorField
              value={resolvedAccentColor}
              showHeader={false}
              disabled={
                formSyncBlocked ||
                isLocked('accentColor') ||
                entitlementsLoading ||
                !canCustomizeAccent
              }
              labels={{
                label: dict.accentColor,
                description: dict.accentColorDescription,
                reset: dict.accentColorReset,
                placeholder: dict.accentColorPlaceholder,
                ctaPreview: {
                  ctaLabel: accentCtaLabel,
                  preview: dict.accentColorCtaPreview,
                  adjustedDarker: dict.accentColorCtaAdjustedDarker,
                  adjustedLighter: dict.accentColorCtaAdjustedLighter,
                },
              }}
              onChange={(next) => setValue('accentColor', next)}
            />
            {!entitlementsLoading && !canCustomizeAccent ? (
              <div className="pt-3">
                <UpgradeCard
                  feature="feature.profile_branding"
                  compact
                  title={dict.accentColorUpgradeTitle}
                  description={dict.accentColorUpgradeDescription}
                />
              </div>
            ) : null}
            <ProfileEditFieldOverrideHint
              fieldKey="accentColor"
              {...overrideHintProps}
            />
            {isLocked('accentColor') ? (
              <ProfileFieldLockedHint locked message={fieldLockedHintMessage} />
            ) : null}
          </SettingsGroupRow>
        </SettingsGroup>
        <SettingsGroupFooter>{dict.accentColorDescription}</SettingsGroupFooter>
      </div>
    </div>
  );
}
